import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import Table from '@risksmart-app/components/src/table';
import type { ParentType } from '@risksmart-app/domain/src/types/consts';
import type { ParentType as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { getResourceTypeForWorkflow } from '@risksmart-app/shared/approvals/workflows';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import TabHeader from 'src/components/tab-header';
import { useManageableApprovalResourceTypes } from 'src/hooks/queries';
import { useIsFeatureFlagEnabled } from 'src/hooks/useIsFeatureFlagEnabled';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useGetApprovalTableProps } from './config';
import ApprovalModal from './modals/ApprovalModal';

type Props = {
  parent?: ObjectWithContributors;
  approvalType?: Parent_Type_Enum;
};

/**
 * Tab for displaying approval
 *
 * @param parent - The object which the approval process is placed on
 * @param approvalType - The object which the approvals are configured from
 *   (defaults to parentType if not provided)
 */
export const ApprovalsTab: FC<Props> = ({ parent, approvalType }) => {
  useI18NSummaryHelpContent(
    parent ? 'approvals.objectLevelHelp' : 'approvals.help'
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [updateApprovalId, setUpdateApprovalId] = useState<null | string>(null);
  const [selectedRowResourceType, setSelectedRowResourceType] = useState<
    ParentType | undefined
  >(undefined);
  const { t } = useTranslation(['common'], { keyPrefix: 'approvals' });

  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const tableProps = useGetApprovalTableProps(parent, (item) => {
    setUpdateApprovalId(item.Id);
    setSelectedRowResourceType(getResourceTypeForWorkflow(item.WorkflowId));
    setModalOpen(true);
  });

  // Legacy (flag-off) gating — unchanged behaviour.
  const {
    hasPermission: canUpdateApproval,
    loading: canUpdateApprovalLoading,
  } = useHasPermissionQuery(`update:${approvalType!}`, parent);
  const legacyHasPermission =
    !canUpdateApprovalLoading && (canUpdateApproval || !approvalType);

  // V3 two-tier gating (matches the data-layer):
  //   - ENTITY tab (approvalType set): parented approvals are gated on update
  //     access to the parent (`rs_node:update` server-side) — OWNERS can
  //     configure approvals for items they own, so the legacy
  //     `update:${approvalType}` check applies under BOTH flag states.
  //   - GLOBAL settings tab: a pure Manager activity — filter by the set of
  //     `${resourceType}_approval` companions the viewer holds.
  const { manageableResourceTypes, loading: manageableLoading } =
    useManageableApprovalResourceTypes();

  const managesAnyGlobal =
    !manageableLoading && (manageableResourceTypes?.length ?? 0) > 0;
  const managesSelectedGlobalRow = selectedRowResourceType
    ? (manageableResourceTypes?.includes(selectedRowResourceType) ?? false)
    : managesAnyGlobal;

  // Create button: entity tab → parent-update gate; global tab (flag on) →
  // viewer manages at least one resource type.
  const canCreate = approvalType
    ? legacyHasPermission
    : trpcEnabled
      ? managesAnyGlobal
      : legacyHasPermission;

  // Per-row edit/delete: entity tab → parent-update gate; global tab (flag
  // on) → the clicked row's workflow-derived resource type.
  const modalReadOnly = approvalType
    ? !legacyHasPermission
    : trpcEnabled
      ? !managesSelectedGlobalRow
      : !legacyHasPermission;

  useEffect(() => {
    if (!modalOpen) {
      setUpdateApprovalId(null);
      setSelectedRowResourceType(undefined);
    }
  }, [modalOpen]);

  return (
    <>
      <Table
        {...tableProps}
        header={
          <SpaceBetween size={'m'}>
            <TabHeader
              actions={
                <SpaceBetween direction={'horizontal'} size={'xs'}>
                  <Button
                    variant={'primary'}
                    formAction={'none'}
                    onClick={() => {
                      setModalOpen(true);
                      setUpdateApprovalId(null);
                      setSelectedRowResourceType(undefined);
                    }}
                    disabled={!canCreate}
                  >
                    {t('add_approval')}
                  </Button>
                </SpaceBetween>
              }
            >
              {t('approvalTableTitle')}
            </TabHeader>
          </SpaceBetween>
        }
        variant={'embedded'}
      />
      <ApprovalModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        approvalId={updateApprovalId}
        parentId={parent?.Id}
        readOnly={modalReadOnly}
      />
    </>
  );
};
